/**
 * Per-route SEO metadata + JSON-LD builders.
 * Keyword map (one primary theme per URL, no cannibalisation):
 *   /                                  web + app development company, Pune/Mumbai/Nashik
 *   /services                          software development services (hub)
 *   /services/website-development      website development company
 *   /services/web-app-development      web application development company
 *   /services/mobile-app-development   mobile app development company
 *   /services/custom-software-...      custom software development company
 *   /services/ecommerce-website-...    ecommerce website development
 *   /services/ai-solutions             AI development company / AI solutions
 *   /website-development-company-<city> city landing pages
 *   data/landings.js                   app city pages, Shopify, app cost
 */
import { SITE, SERVICES } from '@/data/site';
import { CITIES } from '@/data/cities';
import { landingByPath } from '@/data/landings';
import { workByPath } from '@/data/work';

export const SITE_URL = 'https://niodelta.com';
export const OG_IMAGE = `${SITE_URL}/og-image.jpg`;
export const AREAS = ['Pune', 'Mumbai', 'Thane', 'Nashik'];

const BRAND = 'NioDelta';

const STATIC_PAGES = {
  '/': {
    title: `Website & App Development Company in Pune & Mumbai | ${BRAND}`,
    description:
      'NioDelta builds websites, mobile apps, ecommerce stores, custom software and AI tools for businesses across Maharashtra: Pune, Mumbai, Nashik and beyond. Fixed quotes.',
  },
  '/services': {
    title: `Web, App, Ecommerce & AI Development Services | ${BRAND}`,
    description:
      'Website development, web apps, mobile apps, custom software, ecommerce and AI solutions — one studio serving Pune, Mumbai and Nashik. Fixed scope or retainer.',
    breadcrumb: [['Services', '/services']],
  },
  '/work': {
    title: `Our Work: Website, App & Software Case Studies | ${BRAND}`,
    description:
      'Live projects built by NioDelta: Staffly, Shiksha CRM, Foodysh, Rupantaram and ForexPips. Apps, websites and software, each with screenshots and what we built.',
    breadcrumb: [['Work', '/work']],
  },
  '/about': {
    title: `About NioDelta — Software Studio in Pune, Est. 2017`,
    description:
      'NioDelta is a Pune-born software and web development studio, est. 2017. Twelve designers and engineers building for founders across Pune, Mumbai and Nashik.',
    breadcrumb: [['About', '/about']],
  },
  '/website-development-cost': {
    title: `Website Development Cost in Pune, Mumbai & Nashik (2026)`,
    description:
      'Website, ecommerce and app development prices in Pune, Mumbai and Nashik — market rates vs NioDelta packages. Business websites from ₹90,000. Fixed quotes.',
    breadcrumb: [['Pricing', '/website-development-cost']],
  },
  '/contact': {
    title: `Contact NioDelta — Web & App Developers in Pune`,
    description:
      'Talk to a partner, not a salesperson. WhatsApp or email NioDelta for website, app, ecommerce or AI projects in Pune, Mumbai and Nashik. Reply within 1 business day.',
    breadcrumb: [['Contact', '/contact']],
  },
  '/policy': {
    title: `Privacy Policy | ${BRAND}`,
    description:
      'How NioDelta collects, uses, shares and protects personal data on niodelta.com: enquiries, cookies, Google Ads measurement, retention and your rights under Indian law.',
    breadcrumb: [['Privacy Policy', '/policy']],
  },
  '/tos': {
    title: `Terms of Service | ${BRAND}`,
    description:
      'Terms for using niodelta.com: acceptable use, indicative pricing and quotes, intellectual property, third-party links, liability limits and governing law in Pune, India.',
    breadcrumb: [['Terms of Service', '/tos']],
  },
};

const SERVICE_META = {
  'website-development': {
    title: `Website Development Company in Maharashtra | ${BRAND}`,
    description:
      'Custom website design and development for businesses across Maharashtra. Fast, mobile-first, SEO-ready sites on React and Next.js. Business websites from ₹90,000.',
  },
  'web-app-development': {
    title: `Web Application Development Company in Pune & Mumbai | ${BRAND}`,
    description:
      'Custom web app development in Pune and Mumbai: dashboards, portals, SaaS and admin panels with secure auth, realtime data and clean APIs. Built to scale.',
  },
  'mobile-app-development': {
    title: `Mobile App Development Company — Android & iOS | ${BRAND}`,
    description:
      'Android and iOS app development for Indian businesses. Flutter and React Native apps with offline sync, push notifications and Play Store / App Store launch.',
  },
  'custom-software-development': {
    title: `Custom Software Development Company in Pune | ${BRAND}`,
    description:
      'Bespoke software for Pune, Mumbai and Nashik businesses: CRM, ERP add-ons, quoting tools, ops dashboards and integrations that replace spreadsheets for good.',
  },
  'ecommerce-website-development': {
    title: `Ecommerce Website Development in Mumbai & Pune | ${BRAND}`,
    description:
      'Shopify and custom ecommerce website development in Mumbai, Pune and Nashik. UPI & GST-ready checkout, fast product pages and retention built in.',
  },
  'ai-solutions': {
    title: `AI Development Company in Pune & Mumbai — Agents, RAG | ${BRAND}`,
    description:
      'Production AI solutions for Indian businesses: chatbots, RAG search, AI agents and workflow automation with evals, guardrails and cost control. Pune & Mumbai.',
  },
};

export function getPageSeo(pathname) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (STATIC_PAGES[path]) return { path, ...STATIC_PAGES[path] };

  const svc = path.match(/^\/services\/([\w-]+)$/);
  const service = svc && SERVICES.find((s) => s.slug === svc[1]);
  if (service && SERVICE_META[svc[1]]) {
    return {
      path,
      ...SERVICE_META[svc[1]],
      breadcrumb: [['Services', '/services'], [service.title, path]],
      service,
    };
  }

  const city = CITIES.find((c) => c.path === path);
  if (city) {
    return {
      path,
      title: city.seoTitle,
      description: city.seoDescription,
      breadcrumb: [[`Website Development ${city.name}`, path]],
      city,
    };
  }

  const project = workByPath(path);
  if (project) {
    return {
      path,
      title: project.seoTitle,
      description: project.seoDescription,
      breadcrumb: [['Work', '/work'], [project.name, path]],
      project,
    };
  }

  const landing = landingByPath(path);
  if (landing) {
    return {
      path,
      title: landing.seoTitle,
      description: landing.seoDescription,
      breadcrumb: landing.crumbs,
      landing,
    };
  }

  return {
    path,
    title: `Page not found | ${BRAND}`,
    description: 'This page does not exist.',
    noindex: true,
  };
}

export const canonicalUrl = (path) => (path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}/`);

const ORG_ID = `${SITE_URL}/#organization`;

export function organizationSchema() {
  return {
    '@type': 'ProfessionalService',
    '@id': ORG_ID,
    name: BRAND,
    alternateName: 'NioDelta Web Studio',
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/logo.png`,
    image: OG_IMAGE,
    email: SITE.email,
    telephone: SITE.phone,
    foundingDate: '2017',
    priceRange: '₹35,000 – ₹15,00,000',
    description:
      'Website, web app, mobile app, ecommerce and AI development studio serving Pune, Mumbai, Thane and Nashik.',
    areaServed: [
      ...AREAS.map((name) => ({ '@type': 'City', name })),
      { '@type': 'State', name: 'Maharashtra' },
    ],
    knowsAbout: SERVICES.map((s) => s.title),
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: SITE.phone,
      email: SITE.email,
      contactType: 'sales',
      areaServed: 'IN',
      availableLanguage: ['English', 'Hindi', 'Marathi'],
    },
  };
}

function breadcrumbSchema(items) {
  const all = [['Home', '/'], ...items];
  return {
    '@type': 'BreadcrumbList',
    itemListElement: all.map(([name, path], i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: canonicalUrl(path),
    })),
  };
}

function serviceSchema({ name, description, path, areas }) {
  return {
    '@type': 'Service',
    name,
    description,
    url: canonicalUrl(path),
    provider: { '@id': ORG_ID },
    areaServed: areas.map((a) => ({ '@type': 'City', name: a })),
  };
}

export function buildJsonLd(seo) {
  const graph = [organizationSchema()];
  if (seo.path === '/') {
    graph.push({
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: BRAND,
      publisher: { '@id': ORG_ID },
      inLanguage: 'en-IN',
    });
  }
  if (seo.breadcrumb) graph.push(breadcrumbSchema(seo.breadcrumb));
  if (seo.service) {
    graph.push(
      serviceSchema({ name: seo.service.title, description: seo.description, path: seo.path, areas: AREAS })
    );
  }
  if (seo.city) {
    graph.push(
      serviceSchema({
        name: `Website & App Development in ${seo.city.name}`,
        description: seo.description,
        path: seo.path,
        areas: [seo.city.name],
      })
    );
  }
  if (seo.project) {
    graph.push({
      '@type': 'CreativeWork',
      name: seo.project.name,
      url: seo.project.url,
      description: seo.project.summary,
      about: seo.project.sector,
      creator: { '@id': ORG_ID },
      mainEntityOfPage: canonicalUrl(seo.path),
    });
  }
  if (seo.landing?.schemaName) {
    graph.push(
      serviceSchema({
        name: seo.landing.schemaName,
        description: seo.description,
        path: seo.path,
        areas: seo.landing.areas,
      })
    );
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

export function faqSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}
